/**
 * 后端 API 地址。本地开发可在 .env.local 中设置 VITE_API_BASE=http://localhost:8789
 */
const _env = typeof import.meta !== 'undefined' ? (import.meta as { env?: Record<string, string> }).env : undefined;

const DEFAULT_API_BASES = ['https://api.aifashou.cn', 'https://qimen-backend.magicliuxd.workers.dev'];
const LAST_WORKING_API_BASE_KEY = 'qimen_api_base_last_v2';

const normalizeApiBase = (raw: string): string => String(raw || '').trim().replace(/\/+$/, '');

const isLoopbackHost = (rawHost: string): boolean => {
  const host = String(rawHost || '').trim().toLowerCase().replace(/^\[|\]$/g, '');
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
};

const isPrivateIpv4Host = (rawHost: string): boolean => {
  const host = String(rawHost || '').trim().toLowerCase().replace(/^\[|\]$/g, '');
  const match = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!match) return false;
  const nums = match.slice(1).map((v) => Number(v));
  if (nums.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return false;
  const [a, b] = nums;
  if (a === 10) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  return false;
};

const isLocalRuntimeHost = (rawHost: string): boolean => {
  const host = String(rawHost || '').trim().toLowerCase().replace(/^\[|\]$/g, '');
  if (!host) return false;
  if (isLoopbackHost(host)) return true;
  if (isPrivateIpv4Host(host)) return true;
  if (host.endsWith('.local')) return true;
  return false;
};

const getRuntimeHost = (): string => {
  if (typeof window === 'undefined') return '';
  return String(window.location.hostname || '').trim().toLowerCase();
};

const isRuntimeHttps = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.protocol === 'https:';
};

const hasExplicitLocalApiBaseConfig = (): boolean => {
  const configured = normalizeApiBase(String(_env?.VITE_API_BASE || ''));
  if (!configured) return false;
  try {
    return isLocalRuntimeHost(new URL(configured).hostname);
  } catch {
    return false;
  }
};

const isLikelyLocalDevRuntime = (): boolean => {
  if (typeof window === 'undefined') return false;
  const host = String(window.location.hostname || '').trim().toLowerCase();
  if (!isLocalRuntimeHost(host)) return false;

  const port = String(window.location.port || '').trim();
  if (port === '5173' || port === '4173' || port === '3000' || port === '8787' || port === '8789') {
    return true;
  }
  if (hasExplicitLocalApiBaseConfig()) return true;

  const query = String(window.location.search || '');
  if (/[?&](localApi|useLocalApi|devApi)=1(?:&|$)/i.test(query)) {
    return true;
  }
  return false;
};

const isApiBaseAllowedForRuntime = (rawBase: string): boolean => {
  const base = normalizeApiBase(rawBase);
  if (!base) return false;
  if (typeof window === 'undefined') return true;
  const pageHost = getRuntimeHost();
  if (!pageHost) return true;
  try {
    const url = new URL(base);
    const pageIsLocal = isLocalRuntimeHost(pageHost);
    const targetIsLocal = isLocalRuntimeHost(url.hostname);
    if (pageIsLocal && targetIsLocal && !isLikelyLocalDevRuntime()) {
      return false;
    }
    if (!pageIsLocal && targetIsLocal) {
      return false;
    }
    if (isRuntimeHttps() && url.protocol === 'http:' && !targetIsLocal) {
      return false;
    }
    return true;
  } catch {
    return true;
  }
};

const clearLastWorkingApiBase = (): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(LAST_WORKING_API_BASE_KEY);
  } catch {
    // noop
  }
};

const buildLocalBackendBasesForRuntimeHost = (): string[] => {
  if (typeof window === 'undefined') return [];
  if (!isLikelyLocalDevRuntime()) return [];
  const host = String(window.location.hostname || '').trim();
  if (!isLocalRuntimeHost(host)) return [];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  const bases = isLoopbackHost(host)
    ? [`${protocol}//${host}:8789`, `${protocol}//${host}:8787`]
    : [`${protocol}//${host}:8789`, 'http://127.0.0.1:8789', 'http://localhost:8789', `${protocol}//${host}:8787`, 'http://127.0.0.1:8787', 'http://localhost:8787'];
  return dedupeApiBases(bases);
};

const shouldUseRuntimeOriginAsApiCandidate = (): boolean => {
  if (typeof window === 'undefined') return false;
  const host = getRuntimeHost();
  if (!host || isLocalRuntimeHost(host)) return false;
  // Pages 预览域名上的同源转发并不稳定，直接走 Worker 能避免首跳回退带来的明显变慢。
  if (host.endsWith('.pages.dev')) return false;
  return true;
};

const resolveRuntimeApiBase = (raw: string): string => {
  const base = normalizeApiBase(raw);
  if (!base) return '';
  try {
    const url = new URL(base);
    if (typeof window !== 'undefined') {
      const pageHost = getRuntimeHost();
      const pageIsLocal = isLocalRuntimeHost(pageHost);
      if (
        isLoopbackHost(url.hostname)
        && pageHost
        && pageIsLocal
        && (
          !isLoopbackHost(pageHost)
          || normalizeApiBase(url.hostname) !== normalizeApiBase(pageHost)
        )
      ) {
        url.hostname = pageHost;
        url.protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
      }
    }
    const normalized = normalizeApiBase(url.toString());
    return isApiBaseAllowedForRuntime(normalized) ? normalized : '';
  } catch {
    return isApiBaseAllowedForRuntime(base) ? base : '';
  }
};

const loadLastWorkingApiBase = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    const stored = resolveRuntimeApiBase(localStorage.getItem(LAST_WORKING_API_BASE_KEY) || '');
    if (!stored) return '';
    if (isLocalPreviewRuntime()) {
      try {
        const url = new URL(stored);
        const runtimeHost = String(window.location.hostname || '').trim();
        const isLegacyLocal8787 = isLocalRuntimeHost(url.hostname)
          && String(url.port || '') === '8787'
          && runtimeHost
          && normalizeApiBase(url.hostname) === normalizeApiBase(runtimeHost);
        if (isLegacyLocal8787) {
          clearLastWorkingApiBase();
          return '';
        }
      } catch {
        // noop
      }
    }
    if (!isApiBaseAllowedForRuntime(stored)) {
      clearLastWorkingApiBase();
      return '';
    }
    return stored;
  } catch {
    return '';
  }
};

const saveLastWorkingApiBase = (base: string): void => {
  if (typeof window === 'undefined') return;
  const normalized = resolveRuntimeApiBase(base);
  if (!normalized || !isApiBaseAllowedForRuntime(normalized)) {
    clearLastWorkingApiBase();
    return;
  }
  try {
    localStorage.setItem(LAST_WORKING_API_BASE_KEY, normalized);
  } catch {
    // noop
  }
};

const dedupeApiBases = (rawBases: string[]): string[] => {
  const seen = new Set<string>();
  const bases: string[] = [];
  for (const raw of rawBases) {
    const normalized = normalizeApiBase(raw);
    if (!normalized || seen.has(normalized) || !isApiBaseAllowedForRuntime(normalized)) continue;
    seen.add(normalized);
    bases.push(normalized);
  }
  return bases;
};

const extractApiBaseHost = (base: string): string => {
  try {
    return String(new URL(base).hostname || '').trim().toLowerCase();
  } catch {
    return '';
  }
};

const isWorkersDevHost = (rawHost: string): boolean => {
  const host = String(rawHost || '').trim().toLowerCase().replace(/^\[|\]$/g, '');
  return host.endsWith('.workers.dev');
};

const rankApiBaseForRuntime = (base: string): number => {
  if (typeof window === 'undefined') return 0;
  const host = extractApiBaseHost(base);
  if (!host) return 9;

  const runtimeHost = getRuntimeHost();
  const runtimeIsLocal = isLocalRuntimeHost(runtimeHost);
  const targetIsLocal = isLocalRuntimeHost(host);

  if (runtimeIsLocal) return targetIsLocal ? 0 : 4;
  if (targetIsLocal) return 8;
  if (isWorkersDevHost(host)) return 5;
  if (runtimeHost && host === runtimeHost) return 3;
  return 0;
};

const prioritizeApiBasesForRuntime = (rawBases: string[]): string[] => {
  const deduped = dedupeApiBases(rawBases);
  if (deduped.length <= 1) return deduped;
  const scored = deduped.map((base, index) => ({
    base,
    index,
    rank: rankApiBaseForRuntime(base),
  }));
  scored.sort((a, b) => a.rank - b.rank || a.index - b.index);
  return scored.map((item) => item.base);
};

const API_BASE_CANDIDATES = (() => {
  const candidates: string[] = [];
  const configuredBase = resolveRuntimeApiBase(String(_env?.VITE_API_BASE || ''));
  if (configuredBase) candidates.push(configuredBase);

  const lastWorkingBase = loadLastWorkingApiBase();
  if (lastWorkingBase) candidates.push(lastWorkingBase);

  if (typeof window !== 'undefined') {
    const localBackendBases = buildLocalBackendBasesForRuntimeHost();
    if (localBackendBases.length > 0) {
      candidates.push(...localBackendBases);
    }
  }

  DEFAULT_API_BASES.forEach((base) => candidates.push(base));

  if (typeof window !== 'undefined' && shouldUseRuntimeOriginAsApiCandidate()) {
    const origin = normalizeApiBase(window.location.origin || '');
    if (origin) candidates.push(origin);
  }

  return prioritizeApiBasesForRuntime(candidates);
})();

let preferredApiBase = API_BASE_CANDIDATES[0] || DEFAULT_API_BASES[0] || '';

const getApiCandidatesForRequest = (): string[] => {
  const lastWorkingBase = loadLastWorkingApiBase();
  const ordered = prioritizeApiBasesForRuntime([preferredApiBase, lastWorkingBase, ...API_BASE_CANDIDATES]);
  const baseOrdered = ordered.length > 0 ? ordered : [DEFAULT_API_BASES[0]];
  if (!isLocalPreviewRuntime()) return baseOrdered;
  const runtimeLocalBases = buildLocalBackendBasesForRuntimeHost();
  const locals = dedupeApiBases([
    ...runtimeLocalBases,
    ...baseOrdered.filter((base) => {
      try {
        return isLocalRuntimeHost(new URL(base).hostname);
      } catch {
        return false;
      }
    }),
  ]);
  if (locals.length > 0) {
    const remotes = baseOrdered.filter((base) => {
      try {
        return !isLocalRuntimeHost(new URL(base).hostname);
      } catch {
        return true;
      }
    });
    return dedupeApiBases([...locals, ...remotes]);
  }
  return baseOrdered;
};

const isLocalApiBase = (base: string): boolean => {
  try {
    return isLocalRuntimeHost(new URL(base).hostname);
  } catch {
    return false;
  }
};

const pickFastestApiCandidates = (maxParallel: number): string[] => {
  const ordered = getApiCandidatesForRequest();
  if (ordered.length <= maxParallel) return ordered;

  const locals = ordered.filter((base) => isLocalApiBase(base));
  const remotes = ordered.filter((base) => !isLocalApiBase(base));
  if (locals.length === 0 || remotes.length === 0) {
    return ordered.slice(0, maxParallel);
  }

  return dedupeApiBases([
    locals[0],
    remotes[0],
    ...ordered,
  ]).slice(0, maxParallel);
};

const isLocalPreviewRuntime = (): boolean => {
  return isLikelyLocalDevRuntime();
};

const isApiEndpointPath = (endpoint: string): boolean => (
  API_PATH_PREFIXES.some((prefix) => endpoint.startsWith(prefix))
);

const shouldFallbackFromHtmlResponse = (response: Response, endpoint: string): boolean => {
  if (!response.ok) return false;
  if (!isApiEndpointPath(endpoint)) return false;
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  return contentType.includes('text/html') || contentType.includes('application/xhtml+xml');
};

const normalizeApiError = (error: unknown): Error => {
  const msg = error instanceof Error ? (error.message || '') : String(error || '');
  const lower = msg.toLowerCase();
  const name = error instanceof Error ? String(error.name || '').toLowerCase() : '';
  const isAbortLike = name.includes('abort')
    || lower.includes('aborterror')
    || lower.includes('aborted')
    || lower.includes('signal is aborted')
    || lower.includes('interrupted');
  const isTimeoutLike = lower.includes('timeout') || lower.includes('timed out');
  if (isAbortLike || isTimeoutLike) {
    return new Error('请求超时或连接中断，请稍后重试。');
  }
  if (
    lower.includes('failed to fetch')
    || lower.includes('networkerror')
    || lower.includes('network request failed')
  ) {
    return new Error('网络连接失败，请稍后重试。');
  }
  return error instanceof Error ? error : new Error(msg || '网络请求失败，请稍后重试。');
};

const RETRYABLE_API_STATUS = new Set([404, 405, 502, 503, 504]);
const DEFAULT_API_TIMEOUT_MS = 15000;
const CHAT_API_TIMEOUT_MS = 180000;
// 本地 wrangler + 上游模型链路明显更慢，AI 解盘实测可能需要 50~70 秒。
const CHAT_API_LOCAL_TIMEOUT_MS = 120000;
const CHART_INTERPRETER_CHAT_TIMEOUT_MS = 85000;
const CHART_INTERPRETER_FOLLOWUP_CHAT_TIMEOUT_MS = 70000;
const API_PATH_PREFIXES = ['/auth', '/chat', '/pan', '/name', '/activate', '/usage', '/records', '/admin/api'];

const resolveChatApiTimeoutMs = (overrideMs?: number, featureRaw = '', featureModeRaw = ''): number => {
  const override = Number(overrideMs || 0);
  if (Number.isFinite(override) && override > 0) {
    return Math.max(3000, Math.floor(override));
  }
  const base = isLocalPreviewRuntime() ? CHAT_API_LOCAL_TIMEOUT_MS : CHAT_API_TIMEOUT_MS;
  const feature = String(featureRaw || '').trim();
  const featureMode = String(featureModeRaw || '').trim();
  if (feature === 'chartInterpreter') {
    if (featureMode === 'followup') {
      return Math.min(base, CHART_INTERPRETER_FOLLOWUP_CHAT_TIMEOUT_MS);
    }
    return Math.min(base, CHART_INTERPRETER_CHAT_TIMEOUT_MS);
  }
  return base;
};

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  if (typeof AbortController === 'undefined' || timeoutMs <= 0) {
    return fetch(url, init);
  }
  const controller = new AbortController();
  const externalSignal = init.signal ?? null;
  let timeoutTriggered = false;
  const forwardExternalAbort = () => {
    try {
      controller.abort();
    } catch {
      // noop
    }
  };
  if (externalSignal) {
    if (externalSignal.aborted) {
      forwardExternalAbort();
    } else {
      externalSignal.addEventListener('abort', forwardExternalAbort, { once: true });
    }
  }
  const timer = setTimeout(() => {
    timeoutTriggered = true;
    try {
      controller.abort();
    } catch {
      // noop
    }
  }, timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if (timeoutTriggered) {
      const seconds = Math.max(1, Math.round(timeoutMs / 1000));
      throw new Error(`请求超时（${seconds}秒）`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
    if (externalSignal) {
      externalSignal.removeEventListener('abort', forwardExternalAbort);
    }
  }
}

async function readResponseTextWithTimeout(res: Response, timeoutMs = 0): Promise<string> {
  const ms = Number(timeoutMs || 0);
  if (!Number.isFinite(ms) || ms <= 0 || !res.body) {
    return res.text();
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let out = '';
  let timer: ReturnType<typeof setTimeout> | null = null;
  let timeoutTriggered = false;

  try {
    while (true) {
      const chunkPromise = reader.read();
      const chunk = await Promise.race([
        chunkPromise,
        new Promise<ReadableStreamReadResult<Uint8Array>>((_, reject) => {
          timer = setTimeout(() => {
            timeoutTriggered = true;
            try {
              void reader.cancel('timeout');
            } catch {
              // noop
            }
            reject(new Error(`请求超时（${Math.max(1, Math.round(ms / 1000))}秒）`));
          }, ms);
        }),
      ]);
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (chunk.done) break;
      if (chunk.value) {
        out += decoder.decode(chunk.value, { stream: true });
      }
    }
    const tail = decoder.decode();
    if (tail) out += tail;
    return out;
  } catch (error) {
    if (timeoutTriggered) {
      throw new Error(`请求超时（${Math.max(1, Math.round(ms / 1000))}秒）`);
    }
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
    try {
      reader.releaseLock();
    } catch {
      // noop
    }
  }
}

async function apiFetch(path: string, init: RequestInit, options?: { timeoutMs?: number }): Promise<Response> {
  const endpoint = path.startsWith('/') ? path : `/${path}`;
  const timeoutMs = Number(options?.timeoutMs) > 0 ? Number(options?.timeoutMs) : DEFAULT_API_TIMEOUT_MS;
  const candidates = getApiCandidatesForRequest();
  const hasNonLoopbackCandidate = candidates.some((base) => {
    try {
      return !isLoopbackHost(new URL(base).hostname);
    } catch {
      return true;
    }
  });
  let lastError: Error | null = null;

  for (let index = 0; index < candidates.length; index += 1) {
    const base = candidates[index];
    const url = `${base}${endpoint}`;
    const hasNext = index < candidates.length - 1;
    try {
      const response = await fetchWithTimeout(url, init, timeoutMs);
      if (hasNext && shouldFallbackFromHtmlResponse(response, endpoint)) {
        continue;
      }
      if (hasNext && RETRYABLE_API_STATUS.has(response.status)) {
        continue;
      }
      // 本地预览时禁止从本地 403 自动回退到线上，避免调试时“看起来改了却没生效”。
      if (hasNext && response.status === 403 && hasNonLoopbackCandidate && !isLocalPreviewRuntime()) {
        try {
          if (isLoopbackHost(new URL(base).hostname)) {
            continue;
          }
        } catch {
          // ignore parse failures
        }
      }
      preferredApiBase = base;
      saveLastWorkingApiBase(base);
      return response;
    } catch (err) {
      lastError = normalizeApiError(err);
    }
  }

  throw normalizeApiError(lastError || new Error('network_error'));
}

async function apiFetchFastest(
  path: string,
  init: RequestInit,
  options?: { timeoutMs?: number; maxParallel?: number; disableSequentialFallback?: boolean },
): Promise<Response> {
  const endpoint = path.startsWith('/') ? path : `/${path}`;
  const timeoutMs = Number(options?.timeoutMs) > 0 ? Number(options?.timeoutMs) : DEFAULT_API_TIMEOUT_MS;
  const maxParallel = Math.max(2, Math.min(4, Number(options?.maxParallel) || 2));
  const disableSequentialFallback = options?.disableSequentialFallback === true;
  const candidates = pickFastestApiCandidates(maxParallel);
  if (candidates.length <= 1) {
    return apiFetch(endpoint, init, { timeoutMs });
  }

  const controllers: AbortController[] = [];
  const makeAttempt = (base: string) => (async () => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) controllers.push(controller);
    const response = await fetchWithTimeout(
      `${base}${endpoint}`,
      { ...init, signal: controller?.signal ?? init.signal },
      timeoutMs,
    );
    if (shouldFallbackFromHtmlResponse(response, endpoint)) {
      throw new Error('retryable_html_response');
    }
    if (RETRYABLE_API_STATUS.has(response.status)) {
      throw new Error(`retryable_status_${response.status}`);
    }
    return { base, response };
  })();

  try {
    const winner = await Promise.any(candidates.map((base) => makeAttempt(base)));
    preferredApiBase = winner.base;
    saveLastWorkingApiBase(winner.base);
    controllers.forEach((controller) => {
      try {
        controller.abort();
      } catch {
        // noop
      }
    });
    return winner.response;
  } catch (error) {
    if (disableSequentialFallback) {
      if (error && typeof error === 'object' && 'errors' in error && Array.isArray((error as { errors?: unknown[] }).errors)) {
        const detail = (error as { errors: unknown[] }).errors
          .map((item) => (item instanceof Error ? item.message : String(item || '')))
          .find((item) => item && item.trim());
        throw normalizeApiError(new Error(detail || 'network_error'));
      }
      throw normalizeApiError(error);
    }
    return apiFetch(endpoint, init, { timeoutMs });
  }
}

export const API_BASE = API_BASE_CANDIDATES[0] || DEFAULT_API_BASES[0];
console.log('[API_DEBUG] API_BASE:', API_BASE, 'PREFERRED:', preferredApiBase, 'CANDIDATES:', API_BASE_CANDIDATES, 'VITE_API_BASE:', _env?.VITE_API_BASE);
/** 文本推理模型（国内） */
export const TEXT_MODEL = 'deepseek-v3.2';
/** 图像理解模型（国内） */
export const VISION_MODEL = 'Qwen/Qwen2.5-VL-72B-Instruct';

export interface StoredUser {
  userId: string;
  email: string;
  name: string;
  phone?: string;
  registeredAt?: number | null;
  lastLoginAt?: number | null;
  loginCount?: number;
  expireTime?: number | null;
  isMember?: boolean;
  membershipStatus?: string;
  dailyGiftPoints?: number;
  currentPoints?: number;
  memberType?: 'normal' | 'beta' | 'admin' | string;
  isBetaMember?: boolean;
  isAdminMember?: boolean;
  contentUnlocks?: Record<string, number>;
}

const PERMANENT_ADMIN_EMAIL = '930198090@qq.com';
const PERMANENT_ADMIN_EXPIRE_TS = 4102444800000;

const normalizePermanentAdminEmail = (raw: unknown): string => (
  String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
);

const isPermanentAdminUser = (user: Pick<StoredUser, 'userId' | 'email'> | null | undefined): boolean => {
  if (!user) return false;
  const uid = normalizePermanentAdminEmail(user.userId || user.email || '');
  return uid === PERMANENT_ADMIN_EMAIL;
};

const applyPermanentAdminUser = (user: StoredUser): StoredUser => {
  if (!isPermanentAdminUser(user)) return user;
  return {
    ...user,
    memberType: 'admin',
    isAdminMember: true,
    isBetaMember: false,
    isMember: true,
    membershipStatus: '管理员会员',
    expireTime: PERMANENT_ADMIN_EXPIRE_TS,
    currentPoints: Number.isFinite(Number(user.currentPoints))
      ? Math.max(9999999, Number(user.currentPoints))
      : 9999999,
  };
};

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string | (string | { type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string } })[];
}

export interface ChatOptions {
  userId?: string;
  messages: ChatMessage[];
  model?: string;
  jsonMode?: boolean;
  /** 功能标识，用于后端统计与开关控制 */
  feature?: string;
  /** 功能调用模式（如：image / manual），用于差异化积分 */
  featureMode?: string;
  /** 解盘追问会话 id */
  followupSessionId?: string;
  /** 可选：知识库检索关键词/问题（仅后端使用，不在前端拼接） */
  knowledgeQuery?: string;
  /** 可选：用于后端使用记录展示的主问题文本 */
  usageQuestion?: string;
  /** 可选：用于后端使用记录展示的补充提问文本 */
  usageExtraQuestion?: string;
  /** 是否启用流式输出（默认 true） */
  stream?: boolean;
  /** 可选：覆盖默认请求超时时间（毫秒） */
  timeoutMs?: number;
}

export interface TrackFeatureUsageOptions {
  userId?: string;
  feature: string;
  featureName?: string;
  featureMode?: string;
  eventType?: string;
  source?: string;
  points?: number;
  question?: string;
  extraQuestion?: string;
  knowledgeQuery?: string;
  durationMs?: number;
  outputChars?: number;
  streamUsed?: boolean;
  responseMode?: string;
  resultSummary?: string;
  model?: string;
  modelSelector?: string;
  modelSource?: string;
  sessionId?: string;
  pagePath?: string;
  clientTs?: number;
  referrer?: string;
}

export interface FeatureTrialAccessOptions {
  userId?: string;
  feature: string;
  consume?: boolean;
  groupKey?: string;
}

export interface FeatureTrialAccessResult {
  allowed: boolean;
  memberActive: boolean;
  needActivation: boolean;
  usedCount: number;
  limit: number;
  consumed: boolean;
}

export type UserRecordCollectionType = 'pan' | 'sancai';

export interface UserRecordCollectionResponse<T = unknown> {
  success: boolean;
  records: T[];
  updatedAt: number;
}

export interface SubmitClientLogOptions {
  userId?: string;
  source?: string;
  category?: string;
  appLabel?: string;
  message?: string;
  payload?: unknown;
}

export interface SubmitClientLogResult {
  success: boolean;
  logId: string;
  createdAt: string;
  truncated: boolean;
}

interface ChatStreamHandlers {
  onChunk?: (chunk: string) => void;
  onMode?: (mode: 'stream' | 'non_stream') => void;
}

export interface FeatureChatConfig {
  feature: string;
  streamOutputEnabled: boolean;
  wenceSanitizeConfig?: {
    bracketSanitizeEnabled: boolean;
    bracketGuidanceKeywords: string;
    dualLayer?: boolean;
    source?: string;
  } | null;
}

export interface FeatureRemainingItem {
  featureId: string;
  featureName: string;
  limit: number;
  used: number;
  remaining: number;
  unlimited: boolean;
}

export interface FeatureRemainingSummary {
  dateKey: string;
  featureLimitMode: 'count' | 'points';
  totalRemaining: number;
  totalLimit: number;
  totalUsed: number;
  unlimitedFeatureCount: number;
  items: FeatureRemainingItem[];
}

export type PanMode = 'hour' | 'ke';

export interface PanLocationPayload {
  lat: number;
  lng: number;
  accuracy?: number;
  fetchedAt?: number;
}

export interface PanCalculateOptions {
  dateTime: string;
  panMode: PanMode;
  question?: string;
  keIndex?: number;
  manualJu?: number | null;
  useTrueSolarTime?: boolean;
  timezoneOffsetMinutes?: number;
  location?: PanLocationPayload | null;
}

export interface PanCalculateResponse {
  pan: unknown;
  trueSolarInfo: unknown | null;
  sourceDateTime: string;
  calcDateTime: string;
  mode: PanMode;
  keIndex: number;
}

const PAN_CALC_CACHE_MAX = 24;
const panCalcCache = new Map<string, PanCalculateResponse>();

const buildPanCalcCacheKey = (options: PanCalculateOptions): string => JSON.stringify({
  dateTime: options.dateTime,
  panMode: options.panMode,
  question: String(options.question || '').trim(),
  keIndex: Number(options.keIndex || 0),
  manualJu: options.manualJu ?? null,
  useTrueSolarTime: options.useTrueSolarTime === true,
  timezoneOffsetMinutes: Number(options.timezoneOffsetMinutes || 0),
  location: options.location
    ? {
        lat: Number(options.location.lat || 0).toFixed(4),
        lng: Number(options.location.lng || 0).toFixed(4),
      }
    : null,
});

export interface KangxiStrokeLookupItem {
  char: string;
  stroke: number;
  source: 'd1' | 'local' | 'packed' | 'modern' | 'fallback' | 'zdic' | 'nameology' | 'none';
}

export interface KangxiCharLookupResult {
  char: string;
  stroke: number;
  source: 'd1' | 'local' | 'packed' | 'modern' | 'fallback' | 'zdic' | 'nameology' | 'none';
  nameologyStroke?: number;
  simplifiedStroke?: number;
  traditionalStroke?: number;
  traditional?: string;
  pinyin?: string;
  radicals?: string;
  structure?: string;
  wuxing?: string;
  kangxiText: string;
  modernText: string;
  modernMore?: string;
  dictMeta?: {
    generatedAt?: string;
    totalEntries?: number;
    xinhuaPartial?: boolean;
  } | null;
  error?: string;
}

export interface FourHarmPalacePayload {
  position: number;
  trigram?: string;
  gateSymbol?: string;
  heavenlyStem?: string;
  earthlyStem?: string;
  leadStem?: string;
  hasVoid?: boolean;
  trueVoid?: boolean;
  hasMenPo?: boolean;
  heavenlyHasXing?: boolean;
  earthlyHasXing?: boolean;
  earthlyHasXingByFuYin?: boolean;
  heavenlyInTomb?: boolean;
  earthlyInTomb?: boolean;
  trueTomb?: boolean;
  reverseJudgeTriggered?: boolean;
}

interface AuthResult {
  success: boolean;
  user?: StoredUser;
  error?: string;
}

function normalizeJsonText(raw: string): string {
  return String(raw || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '')
    .replace(/^json\s*/i, '')
    .trim();
}

function extractFirstJsonObject(raw: string): string {
  const text = normalizeJsonText(raw);
  if (!text) return '';

  const start = text.indexOf('{');
  if (start < 0) return text;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }
      if (ch === '"') inString = false;
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{') depth += 1;
    if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        return text.slice(start, i + 1).trim();
      }
    }
  }
  return text.slice(start).trim();
}

function parseJsonLoose(raw: string): { ok: true; value: unknown } | { ok: false } {
  const normalized = normalizeJsonText(raw);
  if (!normalized) return { ok: false };
  try {
    return { ok: true, value: JSON.parse(normalized) };
  } catch {
    const extracted = extractFirstJsonObject(normalized);
    if (!extracted) return { ok: false };
    try {
      return { ok: true, value: JSON.parse(extracted) };
    } catch {
      return { ok: false };
    }
  }
}

async function readResponseJson(res: Response, timeoutMs = 0): Promise<unknown> {
  const raw = await readResponseTextWithTimeout(res, timeoutMs);
  const parsed = parseJsonLoose(raw);
  if (parsed.ok) return parsed.value;
  return {};
}

function normalizeContentUnlockId(raw: unknown): string {
  const id = String(raw || '').trim().toLowerCase();
  if (!id || !/^[a-z][a-z0-9:_-]{0,63}$/.test(id)) return '';
  return id;
}

function normalizeContentUnlockMap(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  const now = Date.now();
  const out: Record<string, number> = {};
  for (const [rawKey, rawValue] of Object.entries(raw as Record<string, unknown>)) {
    const key = normalizeContentUnlockId(rawKey);
    if (!key) continue;
    let ts = 0;
    if (typeof rawValue === 'number') {
      ts = rawValue;
    } else if (rawValue && typeof rawValue === 'object') {
      const row = rawValue as Record<string, unknown>;
      ts = Number(row.expireTime ?? row.expiresAt ?? row.expireAt ?? 0);
    } else {
      ts = Number(rawValue || 0);
    }
    if (!Number.isFinite(ts) || ts <= now) continue;
    out[key] = Math.floor(ts);
  }
  return out;
}

function parseStoredUser(raw: unknown): StoredUser | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const userId = typeof r.userId === 'string' ? r.userId : '';
  if (!userId) return null;
  const email = typeof r.email === 'string' && r.email ? r.email : userId;
  const name = typeof r.name === 'string' && r.name ? r.name : email;

  return applyPermanentAdminUser({
    userId,
    email,
    name,
    phone: typeof r.phone === 'string' ? r.phone : '',
    registeredAt: typeof r.registeredAt === 'number' ? r.registeredAt : null,
    lastLoginAt: typeof r.lastLoginAt === 'number' ? r.lastLoginAt : null,
    loginCount: typeof r.loginCount === 'number' ? r.loginCount : 0,
    expireTime: typeof r.expireTime === 'number' ? r.expireTime : null,
    isMember: typeof r.isMember === 'boolean' ? r.isMember : undefined,
    membershipStatus: typeof r.membershipStatus === 'string' ? r.membershipStatus : undefined,
    dailyGiftPoints: typeof r.dailyGiftPoints === 'number' ? r.dailyGiftPoints : undefined,
    currentPoints: typeof r.currentPoints === 'number' ? r.currentPoints : undefined,
    memberType: typeof r.memberType === 'string' ? r.memberType : undefined,
    isBetaMember: typeof r.isBetaMember === 'boolean' ? r.isBetaMember : undefined,
    isAdminMember: typeof r.isAdminMember === 'boolean' ? r.isAdminMember : undefined,
    contentUnlocks: normalizeContentUnlockMap(r.contentUnlocks),
  });
}

export function saveStoredUser(user: StoredUser): void {
  localStorage.setItem('qimen_user', JSON.stringify(applyPermanentAdminUser(user)));
}

export function clearStoredUser(): void {
  localStorage.removeItem('qimen_user');
}

/**
 * 获取本地存储的用户信息
 */
export function getStoredUser(): StoredUser | null {
  try {
    const s = localStorage.getItem('qimen_user');
    if (!s) return null;
    return parseStoredUser(JSON.parse(s));
  } catch {
    return null;
  }
}

export const getChatRuntimeUser = (): StoredUser => {
  const stored = getStoredUser();
  if (stored?.userId) return stored;
  throw new Error('请先登录');
};

export function isProMember(user: StoredUser | null = getStoredUser()): boolean {
  if (!user) return false;
  if (user.isAdminMember || user.memberType === 'admin') return true;
  if (!user.expireTime) return false;
  return Date.now() < user.expireTime;
}

export function hasContentUnlock(contentId: string, user: StoredUser | null = getStoredUser()): boolean {
  if (!user) return false;
  const key = normalizeContentUnlockId(contentId);
  if (!key) return false;
  const map = normalizeContentUnlockMap(user.contentUnlocks || {});
  const ts = Number(map[key] || 0);
  return Number.isFinite(ts) && ts > Date.now();
}

function readApiErrorFromData(data: unknown, fallback: string): string {
  const err = (data && typeof data === 'object') ? (data as Record<string, unknown>) : {};
  const rawError = err.error;
  const nestedMsg = rawError && typeof rawError === 'object'
    ? (rawError as Record<string, unknown>).message
    : null;
  const msg = (
    (typeof rawError === 'string' && rawError)
    || (typeof nestedMsg === 'string' && nestedMsg)
    || (typeof err.message === 'string' && err.message)
    || (typeof err.detail === 'string' && err.detail)
    || fallback
  );
  return msg;
}

function normalizeAuthUser(user: unknown): StoredUser {
  const parsed = parseStoredUser(user);
  if (!parsed) {
    throw new Error('用户信息异常，请重试');
  }
  return parsed;
}

async function authApi(path: '/auth/register' | '/auth/login' | '/auth/sms/login' | '/auth/phone/bind', payload: Record<string, unknown>): Promise<StoredUser> {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }, { timeoutMs: 12000 });

  const data = (await readResponseJson(res)) as AuthResult;
  if (!res.ok || !data.success || !data.user) {
    throw new Error(data.error || '请求失败');
  }

  const user = normalizeAuthUser(data.user);
  saveStoredUser(user);
  return user;
}

const buildVerificationCodeApiCandidates = (): string[] => {
  const ordered = dedupeApiBases([
    String(_env?.VITE_API_BASE || ''),
    ...DEFAULT_API_BASES,
    ...getApiCandidatesForRequest(),
  ]);
  if (typeof window === 'undefined') return ordered;
  const runtimeOrigin = normalizeApiBase(window.location.origin || '');
  if (!runtimeOrigin || isLikelyLocalDevRuntime()) return ordered;
  // 生产域同源通常是静态站点，/auth 很可能返回 405；验证码接口优先走真实后端域名。
  return ordered.filter((base) => base !== runtimeOrigin);
};

export async function registerApi(params: { email: string; password: string; name?: string; wechatId?: string; emailCode?: string; phone?: string; smsCode?: string }): Promise<StoredUser> {
  const payload: Record<string, unknown> = {
    email: params.email.trim().toLowerCase(),
    password: params.password,
    name: params.name?.trim() || undefined,
    wechatId: params.wechatId?.trim() || undefined,
  };
  const emailCode = String(params.emailCode || '').trim();
  const phone = String(params.phone || '').replace(/\D+/g, '');
  const smsCode = String(params.smsCode || '').trim();
  if (emailCode) payload.emailCode = emailCode;
  if (phone) payload.phone = phone;
  if (smsCode) payload.smsCode = smsCode;
  return authApi('/auth/register', payload);
}

export async function loginApi(params: { email: string; password: string }): Promise<StoredUser> {
  return authApi('/auth/login', {
    email: params.email.trim().toLowerCase(),
    password: params.password,
  });
}

export async function sendEmailCodeApi(params: { email: string; scene?: 'register' | 'login' }): Promise<{ ttlSeconds: number; cooldownSeconds: number }> {
  const email = String(params.email || '').trim().toLowerCase();
  const scene = String(params.scene || 'register').trim() || 'register';
  const endpoint = '/auth/email/send-code';
  const requestInit: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, scene }),
  };
  const candidates = buildVerificationCodeApiCandidates();
  let lastError: Error | null = null;
  const SEND_CODE_TIMEOUT_MS = 30000;

  for (let index = 0; index < candidates.length; index += 1) {
    const base = candidates[index];
    const hasNext = index < candidates.length - 1;
    try {
      const res = await fetchWithTimeout(`${base}${endpoint}`, requestInit, SEND_CODE_TIMEOUT_MS);
      const data = await readResponseJson(res) as Record<string, unknown>;
      if (res.ok && data?.success !== false) {
        preferredApiBase = base;
        saveLastWorkingApiBase(base);
        return {
          ttlSeconds: Number(data?.ttlSeconds || 300),
          cooldownSeconds: Number(data?.cooldownSeconds || 60),
        };
      }
      const errMsg = readApiErrorFromData(data, '发送邮箱验证码失败');
      const lower = errMsg.toLowerCase();
      const shouldTryNext = hasNext && (
        res.status === 401
        || res.status === 403
        || res.status === 500
        || lower.includes('unauthorized')
        || lower.includes('not authorized')
      );
      if (shouldTryNext) {
        lastError = new Error(errMsg);
        continue;
      }
      throw new Error(errMsg);
    } catch (err) {
      const normalized = normalizeApiError(err);
      if (hasNext) {
        lastError = normalized;
        continue;
      }
      throw normalized;
    }
  }

  throw normalizeApiError(lastError || new Error('发送邮箱验证码失败'));
}

export async function sendSmsCodeApi(params: { phone: string; scene?: 'login' | 'email_login' | 'register' | 'bind_phone' | 'bind' }): Promise<{ ttlSeconds: number; cooldownSeconds: number }> {
  const phone = String(params.phone || '').replace(/\D+/g, '');
  const scene = String(params.scene || 'login').trim() || 'login';
  const endpoint = '/auth/sms/send-code';
  const requestInit: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, scene }),
  };
  const candidates = buildVerificationCodeApiCandidates();
  let lastError: Error | null = null;
  const SEND_CODE_TIMEOUT_MS = 30000;

  for (let index = 0; index < candidates.length; index += 1) {
    const base = candidates[index];
    const hasNext = index < candidates.length - 1;
    try {
      const res = await fetchWithTimeout(`${base}${endpoint}`, requestInit, SEND_CODE_TIMEOUT_MS);
      const data = await readResponseJson(res) as Record<string, unknown>;
      if (res.ok && data?.success !== false) {
        preferredApiBase = base;
        saveLastWorkingApiBase(base);
        return {
          ttlSeconds: Number(data?.ttlSeconds || 300),
          cooldownSeconds: Number(data?.cooldownSeconds || 60),
        };
      }
      const errMsg = readApiErrorFromData(data, '发送验证码失败');
      const lower = errMsg.toLowerCase();
      const shouldTryNext = hasNext && (
        res.status === 401
        || res.status === 403
        || res.status === 500
        || lower.includes('unauthorized')
        || lower.includes('not authorized')
      );
      if (shouldTryNext) {
        lastError = new Error(errMsg);
        continue;
      }
      throw new Error(errMsg);
    } catch (err) {
      const normalized = normalizeApiError(err);
      if (hasNext) {
        lastError = normalized;
        continue;
      }
      throw normalized;
    }
  }

  throw normalizeApiError(lastError || new Error('发送验证码失败'));
}

export async function smsLoginApi(params: { phone: string; code: string }): Promise<StoredUser> {
  const phone = String(params.phone || '').replace(/\D+/g, '');
  const code = String(params.code || '').trim();
  return authApi('/auth/sms/login', { phone, code });
}

export async function bindPhoneApi(params: { phone: string; code: string }): Promise<StoredUser> {
  const user = getStoredUser();
  if (!user?.userId) {
    throw new Error('请先登录');
  }
  const phone = String(params.phone || '').replace(/\D+/g, '');
  const code = String(params.code || '').trim();
  return authApi('/auth/phone/bind', {
    userId: user.userId,
    phone,
    code,
  });
}

export async function activateMembershipCode(code: string): Promise<StoredUser> {
  const user = getStoredUser();
  if (!user?.userId) {
    throw new Error('请先登录');
  }

  const res = await apiFetch('/activate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: code.trim().toUpperCase(), userId: user.userId, name: user.name }),
  }, { timeoutMs: 12000 });

  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || !data?.success) {
    throw new Error(typeof data?.error === 'string' ? data.error : '激活失败');
  }

  const next: StoredUser = data?.user
    ? normalizeAuthUser(data.user)
    : {
        ...user,
        expireTime: typeof data.expireTime === 'number' ? data.expireTime : user.expireTime || null,
        isMember: true,
        membershipStatus: typeof data.membershipStatus === 'string' ? data.membershipStatus : '会员中',
      };

  saveStoredUser(next);
  return next;
}

export async function refreshStoredUserFromServer(): Promise<StoredUser | null> {
  const user = getStoredUser();
  if (!user?.userId) return null;
  const query = encodeURIComponent(user.userId);
  const res = await apiFetch(`/auth/profile?userId=${query}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  }, { timeoutMs: 12000 });
  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || !data?.success || !data?.user) {
    return null;
  }
  const next = normalizeAuthUser(data.user);
  saveStoredUser(next);
  return next;
}

export async function fetchFeatureRemainingSummary(): Promise<FeatureRemainingSummary> {
  const user = getStoredUser();
  if (!user?.userId) {
    throw new Error('请先登录');
  }
  const query = encodeURIComponent(user.userId);
  const res = await apiFetch(`/auth/feature-remaining?userId=${query}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  }, { timeoutMs: 12000 });
  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || data?.success === false) {
    throw new Error(readApiErrorFromData(data, '获取剩余次数失败'));
  }

  const itemsRaw = Array.isArray(data.items) ? data.items : [];
  const items: FeatureRemainingItem[] = itemsRaw
    .map((item) => (item && typeof item === 'object' ? item as Record<string, unknown> : null))
    .filter((item): item is Record<string, unknown> => Boolean(item))
    .map((item) => {
      const limitNum = Number(item.limit);
      const usedNum = Number(item.used);
      const remainingNum = Number(item.remaining);
      return {
        featureId: String(item.featureId || '').trim(),
        featureName: String(item.featureName || '').trim(),
        limit: Number.isFinite(limitNum) ? limitNum : 0,
        used: Number.isFinite(usedNum) ? usedNum : 0,
        remaining: Number.isFinite(remainingNum) ? remainingNum : 0,
        unlimited: item.unlimited === true,
      };
    })
    .filter((item) => item.featureId);

  const modeRaw = String(data.featureLimitMode || '').trim().toLowerCase();
  const featureLimitMode: 'count' | 'points' = modeRaw === 'count' ? 'count' : 'points';

  return {
    dateKey: String(data.dateKey || ''),
    featureLimitMode,
    totalRemaining: Number.isFinite(Number(data.totalRemaining)) ? Number(data.totalRemaining) : 0,
    totalLimit: Number.isFinite(Number(data.totalLimit)) ? Number(data.totalLimit) : 0,
    totalUsed: Number.isFinite(Number(data.totalUsed)) ? Number(data.totalUsed) : 0,
    unlimitedFeatureCount: Number.isFinite(Number(data.unlimitedFeatureCount)) ? Number(data.unlimitedFeatureCount) : 0,
    items,
  };
}

export async function getFeatureChatConfig(feature: string): Promise<FeatureChatConfig | null> {
  const fid = String(feature || '').trim();
  if (!fid) return null;
  const query = encodeURIComponent(fid);
  const res = await apiFetch(`/chat/feature-config?feature=${query}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  }, { timeoutMs: 8000 });
  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || data?.success === false) return null;
  const featureValue = typeof data.feature === 'string' && data.feature.trim() ? data.feature : fid;
  return {
    feature: featureValue,
    streamOutputEnabled: data.streamOutputEnabled === true,
    wenceSanitizeConfig: data.wenceSanitizeConfig && typeof data.wenceSanitizeConfig === 'object'
      ? {
        bracketSanitizeEnabled: (data.wenceSanitizeConfig as Record<string, unknown>).bracketSanitizeEnabled !== false,
        bracketGuidanceKeywords: String((data.wenceSanitizeConfig as Record<string, unknown>).bracketGuidanceKeywords || ''),
        dualLayer: (data.wenceSanitizeConfig as Record<string, unknown>).dualLayer === true,
        source: String((data.wenceSanitizeConfig as Record<string, unknown>).source || ''),
      }
      : null,
  };
}

export async function trackFeatureUsage(options: TrackFeatureUsageOptions): Promise<boolean> {
  const userId = String(options.userId || getStoredUser()?.userId || '').trim();
  const feature = String(options.feature || '').trim();
  if (!userId || !feature) return false;
  try {
    const res = await apiFetch('/usage/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        feature,
        featureName: String(options.featureName || '').trim() || undefined,
        featureMode: String(options.featureMode || '').trim() || undefined,
        eventType: String(options.eventType || '').trim() || undefined,
        source: String(options.source || '').trim() || undefined,
        points: Number.isFinite(Number(options.points)) ? Number(options.points) : undefined,
        question: String(options.question || '').trim() || undefined,
        extraQuestion: String(options.extraQuestion || '').trim() || undefined,
        knowledgeQuery: String(options.knowledgeQuery || '').trim() || undefined,
        durationMs: Number.isFinite(Number(options.durationMs)) ? Number(options.durationMs) : undefined,
        outputChars: Number.isFinite(Number(options.outputChars)) ? Number(options.outputChars) : undefined,
        streamUsed: options.streamUsed === true ? true : undefined,
        responseMode: String(options.responseMode || '').trim() || undefined,
        resultSummary: String(options.resultSummary || '').trim() || undefined,
        model: String(options.model || '').trim() || undefined,
        modelSelector: String(options.modelSelector || '').trim() || undefined,
        modelSource: String(options.modelSource || '').trim() || undefined,
        sessionId: String(options.sessionId || '').trim() || undefined,
        pagePath: String(options.pagePath || '').trim() || undefined,
        clientTs: Number.isFinite(Number(options.clientTs)) ? Number(options.clientTs) : undefined,
        referrer: String(options.referrer || '').trim() || undefined,
      }),
    }, { timeoutMs: 7000 });
    if (!res.ok) return false;
    const data = await readResponseJson(res) as Record<string, unknown>;
    return data?.success !== false;
  } catch {
    return false;
  }
}

export async function checkFeatureTrialAccess(options: FeatureTrialAccessOptions): Promise<FeatureTrialAccessResult | null> {
  const userId = String(options.userId || getStoredUser()?.userId || '').trim();
  const feature = String(options.feature || '').trim();
  if (!userId || !feature) return null;
  try {
    const res = await apiFetch('/usage/feature-trial-access', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        feature,
        consume: options.consume !== false,
        groupKey: String(options.groupKey || '').trim() || undefined,
      }),
    }, { timeoutMs: 8000 });
    if (!res.ok) return null;
    const data = await readResponseJson(res) as Record<string, unknown>;
    if (data?.success === false) return null;
    return {
      allowed: data?.allowed === true,
      memberActive: data?.memberActive === true,
      needActivation: data?.needActivation === true,
      usedCount: Number.isFinite(Number(data?.usedCount)) ? Number(data?.usedCount) : 0,
      limit: Number.isFinite(Number(data?.limit)) ? Number(data?.limit) : 1,
      consumed: data?.consumed === true,
    };
  } catch {
    return null;
  }
}

export async function fetchUserRecordCollection<T = unknown>(
  type: UserRecordCollectionType,
  userId?: string,
): Promise<UserRecordCollectionResponse<T> | null> {
  const uid = String(userId || getStoredUser()?.userId || '').trim();
  const collectionType = String(type || '').trim() as UserRecordCollectionType;
  if (!uid || (collectionType !== 'pan' && collectionType !== 'sancai')) return null;
  try {
    const res = await apiFetch(`/records/list?userId=${encodeURIComponent(uid)}&type=${encodeURIComponent(collectionType)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }, { timeoutMs: 12000 });
    if (!res.ok) return null;
    const data = await readResponseJson(res) as Record<string, unknown>;
    if (data?.success === false) return null;
    return {
      success: true,
      records: Array.isArray(data?.records) ? data.records as T[] : [],
      updatedAt: Number.isFinite(Number(data?.updatedAt)) ? Number(data.updatedAt) : 0,
    };
  } catch {
    return null;
  }
}

export async function saveUserRecordCollection<T = unknown>(
  type: UserRecordCollectionType,
  records: T[],
  userId?: string,
): Promise<boolean> {
  const uid = String(userId || getStoredUser()?.userId || '').trim();
  const collectionType = String(type || '').trim() as UserRecordCollectionType;
  if (!uid || (collectionType !== 'pan' && collectionType !== 'sancai')) return false;
  try {
    const res = await apiFetch('/records/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: uid,
        type: collectionType,
        records: Array.isArray(records) ? records : [],
      }),
    }, { timeoutMs: 15000 });
    if (!res.ok) return false;
    const data = await readResponseJson(res) as Record<string, unknown>;
    return data?.success !== false;
  } catch {
    return false;
  }
}

const CLIENT_LOG_PAYLOAD_MAX_CHARS = 120000;

const normalizeClientLogPayloadForSubmit = (payload: unknown): unknown => {
  if (payload == null) return null;
  try {
    const raw = JSON.stringify(payload);
    if (raw.length <= CLIENT_LOG_PAYLOAD_MAX_CHARS) return payload;
    return {
      __truncated__: true,
      originalChars: raw.length,
      preview: raw.slice(0, CLIENT_LOG_PAYLOAD_MAX_CHARS),
    };
  } catch (error) {
    return {
      __serialize_error__: true,
      message: error instanceof Error ? error.message : String(error || 'payload_serialize_failed'),
    };
  }
};

export async function submitClientLog(options: SubmitClientLogOptions): Promise<SubmitClientLogResult> {
  const userId = String(options.userId || getStoredUser()?.userId || '').trim();
  if (!userId) throw new Error('请先登录');

  const res = await apiFetchFastest('/usage/client-log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId,
      source: String(options.source || '').trim() || undefined,
      category: String(options.category || '').trim() || undefined,
      appLabel: String(options.appLabel || '').trim() || undefined,
      message: String(options.message || '').trim() || undefined,
      payload: normalizeClientLogPayloadForSubmit(options.payload ?? null),
    }),
  }, { timeoutMs: 10000, maxParallel: 2, disableSequentialFallback: true });
  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || data?.success === false) {
    throw new Error(readApiErrorFromData(data, `提交日志失败（${res.status}）`));
  }
  return {
    success: true,
    logId: String(data.logId || ''),
    createdAt: String(data.createdAt || ''),
    truncated: data.truncated === true,
  };
}

export async function getFourHarmRelief(palace: FourHarmPalacePayload): Promise<{ direction: string; lines: string[] }> {
  const user = getStoredUser();
  if (!user?.userId) {
    throw new Error('请先登录');
  }
  const res = await apiFetch('/pan/four-harm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId: user.userId,
      palace,
    }),
  }, { timeoutMs: 15000 });
  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || data.success === false) {
    throw new Error(readApiErrorFromData(data, '四害化解失败'));
  }
  const linesRaw = Array.isArray(data.lines) ? data.lines : [];
  return {
    direction: typeof data.direction === 'string' ? data.direction : '',
    lines: linesRaw.map((item) => String(item || '').trim()).filter(Boolean),
  };
}

export async function calculatePanApi(options: PanCalculateOptions): Promise<PanCalculateResponse> {
  const cacheKey = buildPanCalcCacheKey(options);
  const cached = panCalcCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const res = await apiFetchFastest('/pan/calculate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      dateTime: options.dateTime,
      panMode: options.panMode,
      question: options.question || '',
      keIndex: options.keIndex,
      manualJu: options.manualJu ?? null,
      useTrueSolarTime: options.useTrueSolarTime === true,
      timezoneOffsetMinutes: options.timezoneOffsetMinutes,
      location: options.location || undefined,
    }),
  }, { timeoutMs: 12000, maxParallel: 3 });

  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || data.success === false) {
    const msg = readApiErrorFromData(data, `排盘失败: ${res.status}`);
    throw new Error(msg);
  }

  const payload: PanCalculateResponse = {
    pan: data.pan,
    trueSolarInfo: data.trueSolarInfo ?? null,
    sourceDateTime: typeof data.sourceDateTime === 'string' ? data.sourceDateTime : '',
    calcDateTime: typeof data.calcDateTime === 'string' ? data.calcDateTime : '',
    mode: data.mode === 'ke' ? 'ke' : 'hour',
    keIndex: Number.isFinite(Number(data.keIndex)) ? Number(data.keIndex) : 0,
  };
  panCalcCache.set(cacheKey, payload);
  if (panCalcCache.size > PAN_CALC_CACHE_MAX) {
    const oldestKey = panCalcCache.keys().next().value as string | undefined;
    if (oldestKey) panCalcCache.delete(oldestKey);
  }
  return payload;
}

function takeFirstCodePoint(raw: string): string {
  const chars = Array.from(String(raw || '').trim());
  return chars.length > 0 ? chars[0] : '';
}

export async function lookupKangxiStrokesApi(chars: string[]): Promise<Record<string, number>> {
  const uniqueChars = Array.from(
    new Set(
      (Array.isArray(chars) ? chars : [])
        .map((item) => takeFirstCodePoint(String(item || '')))
        .filter(Boolean),
    ),
  );
  if (uniqueChars.length === 0) return {};

  const res = await apiFetchFastest('/name/kangxi-strokes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chars: uniqueChars }),
  }, { timeoutMs: 8000, maxParallel: 2 });

  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || data.success === false) {
    throw new Error(readApiErrorFromData(data, '姓名学笔画查询失败'));
  }

  const rows = Array.isArray(data.results) ? data.results : [];
  const map: Record<string, number> = {};
  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const item = row as Partial<KangxiStrokeLookupItem>;
    const char = typeof item.char === 'string' ? item.char : '';
    const stroke = Number(item.stroke);
    if (char && Number.isFinite(stroke) && stroke > 0) {
      map[char] = stroke;
    }
  });

  return map;
}

export async function lookupKangxiCharApi(char: string): Promise<KangxiCharLookupResult> {
  const clean = takeFirstCodePoint(char);
  if (!clean) {
    throw new Error('请输入单字');
  }

  const res = await apiFetchFastest('/name/char-lookup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ char: clean }),
  }, { timeoutMs: 8000, maxParallel: 2 });

  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || data.success === false) {
    throw new Error(readApiErrorFromData(data, '字典查询失败'));
  }

  const raw = (data.result && typeof data.result === 'object')
    ? data.result as Record<string, unknown>
    : {};

  const stroke = Number(raw.stroke);
  const sourceRaw = typeof raw.source === 'string' ? raw.source : 'none';
  const source = (
    sourceRaw === 'd1'
    || sourceRaw === 'local'
    || sourceRaw === 'packed'
    || sourceRaw === 'modern'
    || sourceRaw === 'fallback'
    || sourceRaw === 'zdic'
    || sourceRaw === 'nameology'
  ) ? sourceRaw : 'none';
  const nameologyStroke = Number(raw.nameologyStroke);
  const simplifiedStroke = Number(raw.simplifiedStroke);
  const traditionalStroke = Number(raw.traditionalStroke);

  return {
    char: typeof raw.char === 'string' && raw.char ? raw.char : clean,
    stroke: Number.isFinite(stroke) && stroke > 0 ? stroke : 0,
    source,
    nameologyStroke: Number.isFinite(nameologyStroke) && nameologyStroke > 0 ? nameologyStroke : 0,
    simplifiedStroke: Number.isFinite(simplifiedStroke) && simplifiedStroke > 0 ? simplifiedStroke : 0,
    traditionalStroke: Number.isFinite(traditionalStroke) && traditionalStroke > 0 ? traditionalStroke : 0,
    traditional: typeof raw.traditional === 'string' ? raw.traditional : '',
    pinyin: typeof raw.pinyin === 'string' ? raw.pinyin : '',
    radicals: typeof raw.radicals === 'string' ? raw.radicals : '',
    structure: typeof raw.structure === 'string' ? raw.structure : '',
    wuxing: typeof raw.wuxing === 'string' ? raw.wuxing : '',
    kangxiText: typeof raw.kangxiText === 'string' ? raw.kangxiText : '',
    modernText: typeof raw.modernText === 'string' ? raw.modernText : '',
    modernMore: typeof raw.modernMore === 'string' ? raw.modernMore : '',
    dictMeta: raw.dictMeta && typeof raw.dictMeta === 'object'
      ? raw.dictMeta as {
          generatedAt?: string;
          totalEntries?: number;
          xinhuaPartial?: boolean;
        }
      : null,
    error: typeof raw.error === 'string' ? raw.error : undefined,
  };
}

/**
 * 调用后端 /chat 接口
 * 返回解析后的 JSON 或抛出错误
 */
export async function chatApi(options: ChatOptions): Promise<{ text: string; full?: unknown }> {
  const {
    messages,
    model,
    jsonMode = false,
    feature,
    featureMode,
    followupSessionId,
    knowledgeQuery,
    usageQuestion,
    usageExtraQuestion,
    timeoutMs: timeoutMsOverride,
  } = options;
  const user = getChatRuntimeUser();

  const timeoutMs = resolveChatApiTimeoutMs(timeoutMsOverride, feature, featureMode);
  const startedAt = Date.now();
  const res = await apiFetchFastest('/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId: user.userId,
      messages,
      model: String(model || '').trim() || undefined,
      jsonMode,
      feature: feature || undefined,
      featureMode: featureMode || undefined,
      followupSessionId: followupSessionId || undefined,
      knowledgeQuery: knowledgeQuery || undefined,
      usageQuestion: usageQuestion || undefined,
      usageExtraQuestion: usageExtraQuestion || undefined,
    }),
  }, { timeoutMs, maxParallel: 3, disableSequentialFallback: true });

  const bodyTimeoutMs = Math.max(3000, timeoutMs - (Date.now() - startedAt));
  const data = await readResponseJson(res, bodyTimeoutMs) as Record<string, unknown>;

  if (res.status === 403) {
    const msg = readApiErrorFromData(data, '会员已过期');
    throw new Error(msg);
  }

  if (!res.ok) {
    const msg = readApiErrorFromData(
      data,
      res.status === 429 ? '模型请求过于频繁，请稍后重试。' : `请求失败: ${res.status}`,
    );
    throw new Error(msg);
  }
  const degradedError = typeof data?.error === 'string' ? data.error.trim() : '';
  if (degradedError) {
    throw new Error(degradedError);
  }
  const meta = (data?.meta && typeof data.meta === 'object') ? data.meta as Record<string, unknown> : null;
  if (meta) {
    const stored = getStoredUser();
    if (stored) {
      const updated: StoredUser = {
        ...stored,
        currentPoints: typeof meta.currentPoints === 'number' ? meta.currentPoints : stored.currentPoints,
      };
      saveStoredUser(updated);
    }
  }
  const text = extractChatTextFromResponse(data);
  return { text, full: data };
}

function extractChatTextFromResponse(data: Record<string, unknown>): string {
  if (typeof data?.text === 'string' && data.text.trim()) {
    return data.text;
  }
  const choices = Array.isArray((data as { choices?: unknown }).choices)
    ? (data as { choices: Array<{ message?: { content?: unknown } }> }).choices
    : [];
  const rawContent = choices[0]?.message?.content;
  return typeof rawContent === 'string'
    ? rawContent
    : Array.isArray(rawContent)
      ? rawContent.map((p: { text?: string }) => p?.text ?? '').join('').trim()
      : '';
}

export async function chatApiStream(
  options: ChatOptions,
  handlers: ChatStreamHandlers = {},
): Promise<{ text: string; full?: unknown }> {
  const {
    messages,
    model,
    jsonMode = false,
    feature,
    featureMode,
    followupSessionId,
    knowledgeQuery,
    usageQuestion,
    usageExtraQuestion,
    timeoutMs: timeoutMsOverride,
  } = options;
  const shouldRequestStream = options?.stream === true;
  const user = getChatRuntimeUser();
  if (jsonMode) {
    return chatApi(options);
  }

  const timeoutMs = resolveChatApiTimeoutMs(timeoutMsOverride, feature, featureMode);
  const startedAt = Date.now();
  const getRemainingBudgetMs = (): number => (
    Math.max(0, timeoutMs - (Date.now() - startedAt))
  );
  const resolveRequestTimeoutMs = (): number => (
    Math.max(3000, getRemainingBudgetMs())
  );
  const buildRequestInit = (streamEnabled: boolean): RequestInit => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId: user.userId,
      messages,
      model: String(model || '').trim() || undefined,
      jsonMode: false,
      stream: streamEnabled,
      feature: feature || undefined,
      featureMode: featureMode || undefined,
      followupSessionId: followupSessionId || undefined,
      knowledgeQuery: knowledgeQuery || undefined,
      usageQuestion: usageQuestion || undefined,
      usageExtraQuestion: usageExtraQuestion || undefined,
    }),
  });
  const requestChat = (streamEnabled: boolean, requestTimeoutMs: number): Promise<Response> => (
    apiFetchFastest('/chat', buildRequestInit(streamEnabled), {
      timeoutMs: requestTimeoutMs,
      maxParallel: 3,
      disableSequentialFallback: true,
    })
  );

  let res: Response;
  try {
    res = await requestChat(shouldRequestStream, resolveRequestTimeoutMs());
  } catch (error) {
    if (!shouldRequestStream) {
      throw normalizeApiError(error);
    }
    const remainingMs = getRemainingBudgetMs();
    if (remainingMs <= 3500) {
      throw normalizeApiError(error);
    }
    // 流式链路失败时自动回退非流式，避免问测直接报错。
    res = await requestChat(false, Math.max(3000, remainingMs));
  }

  const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    if (handlers.onMode) handlers.onMode('non_stream');
    const bodyTimeoutMs = Math.max(3000, timeoutMs - (Date.now() - startedAt));
    const data = await readResponseJson(res, bodyTimeoutMs) as Record<string, unknown>;
    if (res.status === 403) {
      throw new Error(readApiErrorFromData(data, '会员已过期'));
    }
    if (!res.ok) {
      const msg = readApiErrorFromData(data, `请求失败: ${res.status}`);
      const lowerMsg = msg.toLowerCase();
      const shouldRetryAsNonStream = shouldRequestStream
        && (
          res.status === 408
          || res.status === 425
          || res.status === 429
          || res.status === 500
          || res.status === 502
          || res.status === 503
          || res.status === 504
          || lowerMsg.includes('timeout')
          || lowerMsg.includes('超时')
          || lowerMsg.includes('stream')
          || lowerMsg.includes('连接失败')
          || lowerMsg.includes('中断')
        );
      if (shouldRetryAsNonStream) {
        const remainingMs = getRemainingBudgetMs();
        if (remainingMs <= 3500) {
          throw new Error(msg);
        }
        if (handlers.onMode) handlers.onMode('non_stream');
        return chatApi({
          ...options,
          jsonMode: false,
          stream: false,
          timeoutMs: Math.max(3000, remainingMs),
        });
      }
      throw new Error(msg);
    }
    const degradedError = typeof data?.error === 'string' ? data.error.trim() : '';
    if (degradedError) {
      throw new Error(degradedError);
    }
    const meta = (data?.meta && typeof data.meta === 'object') ? data.meta as Record<string, unknown> : null;
    if (meta) {
      const stored = getStoredUser();
      if (stored) {
        const updated: StoredUser = {
          ...stored,
          currentPoints: typeof meta.currentPoints === 'number' ? meta.currentPoints : stored.currentPoints,
        };
        saveStoredUser(updated);
      }
    }
    return { text: extractChatTextFromResponse(data), full: data };
  }

  if (res.status === 403) {
    const bodyTimeoutMs = Math.max(3000, timeoutMs - (Date.now() - startedAt));
    const data = await readResponseJson(res, bodyTimeoutMs) as Record<string, unknown>;
    throw new Error(readApiErrorFromData(data, '会员已过期'));
  }
  if (!res.ok) {
    const bodyTimeoutMs = Math.max(3000, timeoutMs - (Date.now() - startedAt));
    const data = await readResponseJson(res, bodyTimeoutMs) as Record<string, unknown>;
    const msg = readApiErrorFromData(data, `请求失败: ${res.status}`);
    const lowerMsg = msg.toLowerCase();
    const shouldRetryAsNonStream = shouldRequestStream
      && (
        res.status === 408
        || res.status === 425
        || res.status === 429
        || res.status === 500
        || res.status === 502
        || res.status === 503
        || res.status === 504
        || lowerMsg.includes('timeout')
        || lowerMsg.includes('超时')
        || lowerMsg.includes('stream')
        || lowerMsg.includes('连接失败')
        || lowerMsg.includes('中断')
      );
    if (shouldRetryAsNonStream) {
      const remainingMs = getRemainingBudgetMs();
      if (remainingMs <= 3500) {
        throw new Error(msg);
      }
      if (handlers.onMode) handlers.onMode('non_stream');
      return chatApi({
        ...options,
        jsonMode: false,
        stream: false,
        timeoutMs: Math.max(3000, remainingMs),
      });
    }
    throw new Error(msg);
  }
  if (!res.body) {
    throw new Error('流式响应为空，请重试');
  }
  if (handlers.onMode) handlers.onMode('stream');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let out = '';
  let readTimer: ReturnType<typeof setTimeout> | null = null;
  let readTimedOut = false;
  try {
    while (true) {
      const { value, done } = await Promise.race([
        reader.read(),
        new Promise<ReadableStreamReadResult<Uint8Array>>((_, reject) => {
          readTimer = setTimeout(() => {
            readTimedOut = true;
            try {
              void reader.cancel('timeout');
            } catch {
              // noop
            }
            reject(new Error(`请求超时（${Math.max(1, Math.round(timeoutMs / 1000))}秒）`));
          }, timeoutMs);
        }),
      ]);
      if (readTimer) {
        clearTimeout(readTimer);
        readTimer = null;
      }
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      if (!chunk) continue;
      out += chunk;
      if (handlers.onChunk) handlers.onChunk(chunk);
    }
    const tail = decoder.decode();
    if (tail) {
      out += tail;
      if (handlers.onChunk) handlers.onChunk(tail);
    }
  } catch (err) {
    if (readTimedOut) {
      throw new Error(`请求超时（${Math.max(1, Math.round(timeoutMs / 1000))}秒）`);
    }
    throw normalizeApiError(err);
  } finally {
    if (readTimer) clearTimeout(readTimer);
    try {
      reader.releaseLock();
    } catch {
      // noop
    }
  }

  return { text: out };
}

/**
 * 检查用户是否已登录
 */
export function isUserValid(): boolean {
  const u = getStoredUser();
  return Boolean(u?.userId);
}

/**
 * 提示词优化 API
 */
export interface PromptOptimizationOptions {
  currentPrompt: string;
  optimizationSuggestion: string;
  panData?: string;
  model?: string;
}

const ADMIN_SECRET_STORAGE_KEY = 'adminSecret';

const getAdminSecret = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    const querySecret = new URLSearchParams(window.location.search).get('adminSecret');
    if (querySecret && querySecret.trim()) return querySecret.trim();
  } catch {
    // noop
  }
  try {
    return String(localStorage.getItem(ADMIN_SECRET_STORAGE_KEY) || '').trim();
  } catch {
    return '';
  }
};

const buildAdminHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const secret = getAdminSecret();
  if (secret) headers['X-Admin-Secret'] = secret;
  return headers;
};

export interface AdminOptimizeFeaturePromptOptions {
  featureId: string;
  suggestion: string;
  currentPrompt: string;
  question?: string;
  dataText?: string;
  lastOutput?: string;
  knowledgeRefsText?: string;
  model?: string;
}

export interface AdminOptimizeFeaturePromptResult {
  featureId: string;
  model?: string;
  optimizedPrompt: string;
  changeNotes: string[];
  unchanged: boolean;
}

export async function optimizeFeaturePromptAsAdmin(
  options: AdminOptimizeFeaturePromptOptions,
): Promise<AdminOptimizeFeaturePromptResult> {
  const payload = {
    featureId: String(options.featureId || '').trim(),
    suggestion: String(options.suggestion || '').trim(),
    currentPrompt: String(options.currentPrompt || ''),
    question: String(options.question || '').trim(),
    dataText: String(options.dataText || ''),
    lastOutput: String(options.lastOutput || ''),
    knowledgeRefsText: String(options.knowledgeRefsText || ''),
    model: String(options.model || '').trim(),
  };
  if (!payload.featureId) throw new Error('缺少 featureId');
  if (!payload.suggestion) throw new Error('请先填写优化建议');
  if (!payload.currentPrompt.trim()) throw new Error('当前提示词为空，无法优化');

  const res = await apiFetch('/admin/api/feature-prompts/optimize', {
    method: 'POST',
    headers: buildAdminHeaders(),
    body: JSON.stringify(payload),
  }, { timeoutMs: 120000 });
  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || !data?.success) {
    if (res.status === 401) {
      throw new Error('管理鉴权失败：请先在 /admin 页面输入管理密钥后重试。');
    }
    throw new Error(readApiErrorFromData(data, `优化失败（${res.status}）`));
  }

  const optimizedPrompt = String(data.optimizedPrompt || '').trim();
  if (!optimizedPrompt) throw new Error('优化失败：未返回优化后的提示词');
  const changeNotes = Array.isArray(data.changeNotes)
    ? data.changeNotes.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  return {
    featureId: String(data.featureId || payload.featureId),
    model: String(data.model || ''),
    optimizedPrompt,
    changeNotes,
    unchanged: data.unchanged === true,
  };
}

export interface AdminSaveFeaturePromptOptions {
  featureId: string;
  prompt: string;
}

export interface AdminSaveFeaturePromptResult {
  featureId: string;
  hasCustom: boolean;
  prompt: string;
  effectivePrompt: string;
  effectiveSource: 'custom' | 'default';
}

export async function saveFeaturePromptAsAdmin(
  options: AdminSaveFeaturePromptOptions,
): Promise<AdminSaveFeaturePromptResult> {
  const featureId = String(options.featureId || '').trim();
  const prompt = String(options.prompt || '').trim();
  if (!featureId) throw new Error('缺少 featureId');
  if (!prompt) throw new Error('提示词为空，无法保存');

  const res = await apiFetch('/admin/api/feature-prompts', {
    method: 'POST',
    headers: buildAdminHeaders(),
    body: JSON.stringify({
      featureId,
      prompt,
      resetDefault: false,
    }),
  }, { timeoutMs: 20000 });
  const data = await readResponseJson(res) as Record<string, unknown>;
  if (!res.ok || !data?.success) {
    if (res.status === 401) {
      throw new Error('管理鉴权失败：请先在 /admin 页面输入管理密钥后重试。');
    }
    throw new Error(readApiErrorFromData(data, `保存失败（${res.status}）`));
  }

  return {
    featureId: String(data.featureId || featureId),
    hasCustom: data.hasCustom === true,
    prompt: String(data.prompt || prompt),
    effectivePrompt: String(data.effectivePrompt || prompt),
    effectiveSource: String(data.effectiveSource || 'custom') === 'default' ? 'default' : 'custom',
  };
}

export async function optimizePrompt(options: PromptOptimizationOptions): Promise<{ optimizedPrompt: string }> {
  const { currentPrompt, optimizationSuggestion, panData, model = TEXT_MODEL } = options;
  const user = getStoredUser();
  if (!user?.userId) {
    throw new Error('请先登录');
  }

  const systemPrompt = `你是一个提示词优化专家。请根据用户的优化建议，改进原始提示词，使其更加精确、清晰或更具针对性。

要求：
1. 直接输出优化后的提示词，不要添加解释或前缀
2. 保持原始提示词的核心意图
3. 融入用户的优化建议
4. 输出格式为纯文本提示词
5. 如果优化建议不合理，可以适当调整但保留核心内容`;

  const userPrompt = `原始提示词：
${currentPrompt}

优化建议：
${optimizationSuggestion}

${panData ? `\n参考盘面信息：\n${panData}` : ''}

请输出优化后的提示词：`;

  const resp = await fetch(`${API_BASE}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.userId}`,
    },
    body: JSON.stringify({
      model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.7,
      max_tokens: 2000,
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`优化失败: ${errText}`);
  }

  const data = await resp.json() as {
    choices?: Array<{ message?: { content?: string } }>;
    error?: { message?: string };
  };

  if (data.error) {
    throw new Error(data.error.message || '优化失败');
  }

  const optimizedPrompt = data.choices?.[0]?.message?.content?.trim() || '';
  if (!optimizedPrompt) {
    throw new Error('未获取到优化后的提示词');
  }

  return { optimizedPrompt };
}
